import type { AuthenticityVerdict } from "@repo/types/authenticity-check";
import { Badge } from "@repo/ui/badge";
import { cn } from "@repo/ui/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@repo/ui/tooltip";
import type { SnapshotAuthenticityCheck } from "@/lib/api";

const VERDICT_STYLES: Record<AuthenticityVerdict, string> = {
  genuine: "border-transparent bg-emerald-600 text-white hover:bg-emerald-600/80",
  suspicious: "border-transparent bg-amber-500 text-black hover:bg-amber-500/80",
  likely_fake:
    "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
};

export function verdictLabel(verdict: AuthenticityVerdict): string {
  return verdict.replace("_", " ");
}

/** Jev's latest score for a snapshot, coloured by verdict, with the reasons in a tooltip. */
export function JevCheckBadge({ check }: { check: SnapshotAuthenticityCheck | undefined }) {
  if (!check) return <span className="text-xs text-muted-foreground">Not checked</span>;
  const verdict = check.score === null ? null : check.verdict;
  const lines = verdict ? check.reasons : [check.error ?? "No verdict returned"];
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex">
          <Badge
            variant={verdict ? undefined : "outline"}
            className={cn("cursor-default whitespace-nowrap", verdict && VERDICT_STYLES[verdict])}
          >
            {verdict ? `${check.score?.toFixed(2)} ${verdictLabel(verdict)}` : "Check failed"}
          </Badge>
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-sm space-y-1 text-xs">
        <ul className="list-disc space-y-1 pl-4">
          {lines.length === 0 ? (
            <li>No reasons given</li>
          ) : (
            lines.map((line) => <li key={line}>{line}</li>)
          )}
        </ul>
        <p className="text-muted-foreground">
          {check.model} · {new Date(check.checked_at).toLocaleString()}
        </p>
      </TooltipContent>
    </Tooltip>
  );
}
